import React from 'react';

/**
 * Robust Lazy-Loading & Infinite Scroll Controller for Tables.
 */
export function useTableVirtualizer({
    enabled = false,
    items = [],
    scrollContainerRef,
    onLoadMore = null,
    hasMore = false,
    isLoadingMore = false,
    loadMoreThreshold = 250 // px from bottom
}) {
    // Keep scroll listener active only when enabled (See All mode)
    React.useEffect(() => {
        if (!enabled) return;

        const el = scrollContainerRef?.current;
        if (!el) return;

        let ticking = false;
        const handleScroll = () => {
            if (!ticking) {
                window.requestAnimationFrame(() => {
                    if (el) {
                        const top = el.scrollTop || 0;
                        const scrollHeight = el.scrollHeight || 0;
                        const clientHeight = el.clientHeight || 0;

                        // Trigger lazy loading of next batch when near bottom
                        if (
                            onLoadMore &&
                            hasMore &&
                            !isLoadingMore &&
                            scrollHeight - (top + clientHeight) <= loadMoreThreshold
                        ) {
                            onLoadMore();
                        }
                    }
                    ticking = false;
                });
                ticking = true;
            }
        };

        el.addEventListener('scroll', handleScroll, { passive: true });

        return () => {
            el.removeEventListener('scroll', handleScroll);
        };
    }, [enabled, scrollContainerRef, onLoadMore, hasMore, isLoadingMore, loadMoreThreshold]);

    return {
        isVirtual: false,
        visibleItems: items || [],
        topPadding: 0,
        bottomPadding: 0,
        startIndex: 0,
        endIndex: items?.length || 0,
        totalCount: items?.length || 0
    };
}

export default useTableVirtualizer;
